// VerantwortlichkeitDropdown.tsx — Mehrfachauswahl der Verantwortlichkeiten.
//
// Ersetzt die Fluent-`Combobox`, in der man nicht suchen konnte: dort war der
// Eingabewert auf die zusammengesetzten Labels festgenagelt und jede Eingabe
// wurde verworfen. Hier ist die Suche ein eigenes Feld im Panel — Leerzeichen
// sind normale Zeichen, gewählt wird ausschließlich per Klick oder Enter.
//
// Das Panel hängt in einem Portal an `document.body` und ist `position: fixed`
// am Trigger ausgerichtet: die Komponente sitzt in Dialogen und in scrollenden
// Cleanup-Karten, ein `absolute`-Panel würde dort abgeschnitten.
//
// Aufklapprichtung: bevorzugt nach unten. Reicht der Platz dort nicht für die
// Mindesthöhe und ist oberhalb mehr Platz, klappt es nach oben — vorher blieb es
// zwingend unten (PO 10.08.2026) und ragte in tief sitzenden Dialogen unter den
// Bildschirmrand, sodass die unteren Einträge nicht erreichbar waren
// (PO 15.08.2026).

import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { REFINED } from "../risikoanalyseStep/tokens";
import { bereichIcon } from "./bereichIcons";
import {
    filterOptions,
    flatten,
    groupOptions,
    moveActive,
    toggleValue,
    type VerantOption,
} from "./verantwortlichkeitDropdownLogik";
import { alleDokumente, alleFenster, aktivesFenster } from "../windowManager/browserFenster/fensterRegister";

/** Höhe des Panels: bevorzugt, Untergrenze bei wenig Platz. */
const PANEL_MAX = 300;
const PANEL_MIN = 200;
/** Luft zwischen Trigger und Panel bzw. zum Fensterrand. */
const ABSTAND = 4;
const RAND = 12;

const useStyles = makeStyles({
    trigger: {
        display: "flex",
        alignItems: "center",
        columnGap: "8px",
        width: "100%",
        minHeight: "38px",
        boxSizing: "border-box",
        textAlign: "left",
        ...shorthands.padding("6px", "10px"),
        ...shorthands.border("1px", "solid", REFINED.line),
        ...shorthands.borderRadius("8px"),
        backgroundColor: REFINED.white,
        fontFamily: REFINED.fontUI,
        fontSize: "0.86rem",
        color: REFINED.ink,
        cursor: "pointer",
        ":focus-visible": {
            ...shorthands.borderColor(REFINED.aub),
            outlineStyle: "none",
            boxShadow: "0 0 0 3px rgba(125,33,105,0.13)",
        },
    },
    triggerOffen: { ...shorthands.borderColor(REFINED.aub) },
    triggerDisabled: { backgroundColor: REFINED.aub50, color: REFINED.ink3, cursor: "not-allowed" },
    chips: { display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: "6px", rowGap: "5px", flexGrow: 1, minWidth: 0 },
    chip: {
        display: "inline-flex",
        alignItems: "center",
        columnGap: "6px",
        maxWidth: "100%",
        ...shorthands.padding("3px", "6px", "3px", "8px"),
        ...shorthands.border("1px", "solid", REFINED.aub100),
        ...shorthands.borderRadius("999px"),
        backgroundColor: REFINED.aub50,
        color: REFINED.aub,
        fontSize: "0.78rem",
        fontWeight: 600,
        lineHeight: 1.3,
    },
    chipText: { overflowX: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    chipX: {
        appearance: "none",
        ...shorthands.border("0"),
        backgroundColor: "transparent",
        color: REFINED.aub,
        cursor: "pointer",
        fontSize: "0.9rem",
        lineHeight: 1,
        ...shorthands.padding("0", "1px"),
        ":hover": { color: REFINED.aub700 },
    },
    platzhalter: { color: REFINED.ink3, flexGrow: 1 },
    chevron: { color: REFINED.ink3, flexShrink: 0, display: "inline-flex" },

    panel: {
        position: "fixed",
        zIndex: 100000,
        display: "flex",
        flexDirection: "column",
        backgroundColor: REFINED.white,
        ...shorthands.border("1px", "solid", REFINED.line),
        ...shorthands.borderRadius("10px"),
        boxShadow: REFINED.cardShadow,
        overflowX: "hidden",
        overflowY: "hidden",
    },
    sucheWrap: { ...shorthands.padding("8px"), ...shorthands.borderBottom("1px", "solid", REFINED.line), flexShrink: 0 },
    suche: {
        width: "100%",
        boxSizing: "border-box",
        ...shorthands.padding("7px", "10px"),
        ...shorthands.border("1px", "solid", REFINED.line),
        ...shorthands.borderRadius("7px"),
        fontFamily: REFINED.fontUI,
        fontSize: "0.86rem",
        color: REFINED.ink,
        outlineStyle: "none",
        ":focus": { ...shorthands.borderColor(REFINED.aub), boxShadow: "0 0 0 3px rgba(125,33,105,0.13)" },
    },
    liste: { overflowY: "auto", overflowX: "hidden", paddingTop: "4px", paddingBottom: "4px", flexGrow: 1 },
    gruppe: {
        position: "sticky",
        top: 0,
        zIndex: 1,
        display: "flex",
        alignItems: "center",
        columnGap: "7px",
        ...shorthands.padding("6px", "12px"),
        backgroundColor: REFINED.aub50,
        color: REFINED.aub,
        fontFamily: REFINED.fontUI,
        fontSize: "0.72rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    },
    opt: {
        display: "flex",
        alignItems: "center",
        columnGap: "9px",
        width: "100%",
        boxSizing: "border-box",
        textAlign: "left",
        appearance: "none",
        ...shorthands.border("0"),
        backgroundColor: "transparent",
        ...shorthands.padding("8px", "12px"),
        fontFamily: REFINED.fontUI,
        fontSize: "0.9rem",
        color: REFINED.ink,
        cursor: "pointer",
    },
    optAktiv: { backgroundColor: REFINED.aub50 },
    optGewaehlt: { fontWeight: 700, color: REFINED.aub },
    haken: {
        width: "16px",
        height: "16px",
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: REFINED.aub,
    },
    optIcon: { color: REFINED.ink3, display: "inline-flex", flexShrink: 0 },
    optText: { flexGrow: 1, minWidth: 0, overflowWrap: "anywhere" },
    leer: { ...shorthands.padding("12px"), fontFamily: REFINED.fontUI, fontSize: "0.86rem", color: REFINED.ink3 },
    fuss: {
        display: "flex",
        justifyContent: "flex-end",
        flexShrink: 0,
        ...shorthands.padding("8px"),
        ...shorthands.borderTop("1px", "solid", REFINED.line),
    },
    fertig: {
        ...shorthands.border("0"),
        ...shorthands.borderRadius("8px"),
        ...shorthands.padding("7px", "16px"),
        backgroundColor: REFINED.aub,
        color: REFINED.white,
        fontFamily: REFINED.fontUI,
        fontWeight: 700,
        fontSize: "0.84rem",
        cursor: "pointer",
        ":hover": { backgroundColor: REFINED.aub700 },
        ":focus-visible": { outlineStyle: "solid", outlineWidth: "2px", outlineColor: REFINED.aub, outlineOffset: "2px" },
    },
});

const Chevron: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M4 6l4 4 4-4" />
    </svg>
);

const Haken: React.FC = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M3.2 8.4l3 3 6.6-7" />
    </svg>
);

export function VerantwortlichkeitDropdown({
    options,
    values,
    onChange,
    idBase,
    placeholder = "Verantwortlichkeit(en) wählen",
    searchPlaceholder = "Suchen … (z. B. Fuhrpark)",
    emptyText = "Keine Treffer.",
    loading = false,
    disabled = false,
}: {
    options: VerantOption[];
    /** Gewählte Werte (Option.value). */
    values: string[];
    onChange: (next: string[]) => void;
    idBase: string;
    placeholder?: string;
    searchPlaceholder?: string;
    emptyText?: string;
    loading?: boolean;
    disabled?: boolean;
}) {
    const s = useStyles();
    const [offen, setOffen] = React.useState(false);
    const [query, setQuery] = React.useState("");
    const [active, setActive] = React.useState(-1);
    const [pos, setPos] = React.useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);

    const triggerRef = React.useRef<HTMLDivElement>(null);
    const panelRef = React.useRef<HTMLDivElement>(null);
    const sucheRef = React.useRef<HTMLInputElement>(null);
    const listeRef = React.useRef<HTMLDivElement>(null);

    const gesperrt = disabled || loading;

    const gruppen = React.useMemo(() => groupOptions(filterOptions(options, query)), [options, query]);
    const sichtbar = React.useMemo(() => flatten(gruppen), [gruppen]);

    const labelFor = React.useMemo(() => {
        const m = new Map(options.map((o) => [o.value, o.label]));
        return (v: string) => m.get(v) ?? v;
    }, [options]);

    // Panel am Trigger ausrichten. Bevorzugt nach unten; nur wenn dort die
    // Mindesthöhe nicht mehr passt UND oberhalb mehr Platz ist, klappt es nach
    // oben. So bleibt die gewohnte Richtung, solange sie funktioniert.
    const messen = React.useCallback(() => {
        const r = triggerRef.current?.getBoundingClientRect();
        if (!r) return;
        const platzUnten = aktivesFenster().innerHeight - r.bottom - ABSTAND - RAND;
        const platzOben = r.top - ABSTAND - RAND;
        const nachOben = platzUnten < PANEL_MIN && platzOben > platzUnten;
        const maxHeight = Math.max(
            PANEL_MIN,
            Math.min(PANEL_MAX, nachOben ? platzOben : platzUnten),
        );
        setPos({
            // Beim Aufklappen nach oben endet das Panel knapp über dem Trigger;
            // `Math.max` verhindert, dass es im Extremfall über den oberen Rand
            // hinausrutscht und der Anfang der Liste unerreichbar wird.
            top: nachOben ? Math.max(RAND, r.top - ABSTAND - maxHeight) : r.bottom + ABSTAND,
            left: r.left,
            width: r.width,
            maxHeight,
        });
    }, []);

    React.useEffect(() => {
        if (!offen) return;
        messen();
        const onScroll = () => messen();
        alleFenster.addEventListener("scroll", onScroll, true);
        alleFenster.addEventListener("resize", onScroll);
        const onDown = (e: MouseEvent) => {
            const t = e.target as Node;
            if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
            setOffen(false);
        };
        alleDokumente.addEventListener("pointerdown", onDown);
        return () => {
            alleFenster.removeEventListener("scroll", onScroll, true);
            alleFenster.removeEventListener("resize", onScroll);
            alleDokumente.removeEventListener("pointerdown", onDown);
        };
    }, [offen, messen]);

    // Fokus ins Suchfeld, sobald das Panel steht.
    React.useEffect(() => {
        if (offen && pos) sucheRef.current?.focus();
    }, [offen, pos]);

    // Aktive Zeile in den sichtbaren Bereich scrollen. Optionaler Aufruf, weil
    // scrollIntoView nicht überall existiert (jsdom, ältere Umgebungen) — ein
    // fehlender Scroll darf die Auswahl nie zum Absturz bringen.
    React.useEffect(() => {
        if (active < 0) return;
        listeRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`)?.scrollIntoView?.({ block: "nearest" });
    }, [active]);

    const oeffnen = () => {
        if (gesperrt) return;
        setQuery("");
        setActive(-1);
        setOffen(true);
    };

    const schliessen = () => {
        setOffen(false);
        setQuery("");
        setActive(-1);
        triggerRef.current?.focus();
    };

    const toggle = (v: string) => onChange(toggleValue(values, v));

    const onSucheKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => moveActive(a, e.key === "ArrowDown" ? 1 : -1, sichtbar.length));
            return;
        }
        if (e.key === "Enter") {
            e.preventDefault();
            const ziel = active >= 0 ? sichtbar[active] : sichtbar.length === 1 ? sichtbar[0] : undefined;
            if (ziel) toggle(ziel.value);
            return;
        }
        if (e.key === "Escape") {
            e.preventDefault();
            schliessen();
            return;
        }
        // Leeres Suchfeld + Rücktaste → letzten Chip entfernen.
        if (e.key === "Backspace" && !query && values.length) {
            e.preventDefault();
            onChange(values.slice(0, -1));
        }
        // Leertaste bleibt bewusst unbehandelt — sie schreibt ein Zeichen.
    };

    return (
        <>
            <div
                ref={triggerRef}
                role="combobox"
                aria-expanded={offen}
                aria-haspopup="listbox"
                aria-controls={`${idBase}-panel`}
                aria-disabled={gesperrt || undefined}
                tabIndex={gesperrt ? -1 : 0}
                id={`${idBase}-trigger`}
                className={mergeClasses(s.trigger, offen && s.triggerOffen, gesperrt && s.triggerDisabled)}
                onClick={() => (offen ? setOffen(false) : oeffnen())}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
                        e.preventDefault();
                        oeffnen();
                    }
                }}
            >
                {values.length === 0 ? (
                    <span className={s.platzhalter}>{loading ? "Lade Katalog …" : placeholder}</span>
                ) : (
                    <span className={s.chips}>
                        {values.map((v) => (
                            <span key={v} className={s.chip}>
                                <span className={s.chipText}>{labelFor(v)}</span>
                                <button
                                    type="button"
                                    className={s.chipX}
                                    aria-label={`${labelFor(v)} entfernen`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onChange(values.filter((x) => x !== v));
                                    }}
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                    </span>
                )}
                <span className={s.chevron}>
                    <Chevron />
                </span>
            </div>

            {offen &&
                pos &&
                createPortal(
                    <div
                        ref={panelRef}
                        id={`${idBase}-panel`}
                        className={s.panel}
                        style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
                    >
                        <div className={s.sucheWrap}>
                            <input
                                ref={sucheRef}
                                className={s.suche}
                                value={query}
                                placeholder={searchPlaceholder}
                                autoComplete="off"
                                aria-label="Verantwortlichkeiten durchsuchen"
                                onChange={(e) => {
                                    setQuery(e.target.value);
                                    setActive(-1);
                                }}
                                onKeyDown={onSucheKey}
                            />
                        </div>

                        <div className={s.liste} ref={listeRef} role="listbox" aria-multiselectable>
                            {sichtbar.length === 0 ? (
                                <div className={s.leer}>{emptyText}</div>
                            ) : (
                                gruppen.map((g) => (
                                    <div key={g.bereich}>
                                        <div className={s.gruppe}>
                                            {bereichIcon(g.bereich)}
                                            <span>{g.bereich}</span>
                                        </div>
                                        {g.options.map((o) => {
                                            const idx = sichtbar.indexOf(o);
                                            const gewaehlt = values.includes(o.value);
                                            return (
                                                <button
                                                    key={o.value}
                                                    type="button"
                                                    role="option"
                                                    aria-selected={gewaehlt}
                                                    data-idx={idx}
                                                    className={mergeClasses(
                                                        s.opt,
                                                        idx === active && s.optAktiv,
                                                        gewaehlt && s.optGewaehlt,
                                                    )}
                                                    onMouseEnter={() => setActive(idx)}
                                                    onClick={() => toggle(o.value)}
                                                >
                                                    <span className={s.haken}>{gewaehlt && <Haken />}</span>
                                                    <span className={s.optIcon}>{bereichIcon(o.bereich)}</span>
                                                    <span className={s.optText}>{o.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                ))
                            )}
                        </div>

                        {/* „Fertig" schließt die Auswahl (User 28.09.2026) — vorher
                            ging das nur über einen Klick daneben oder Escape, und
                            das Panel verdeckte im Popup die Felder darunter. */}
                        <div className={s.fuss}>
                            <button type="button" className={s.fertig} onClick={schliessen}>
                                Fertig
                            </button>
                        </div>
                    </div>,
                    document.body,
                )}
        </>
    );
}
